import { useState, useEffect, useMemo } from 'react';
import { Activity, Edit2, Trash2, Heart, Thermometer, Weight, Droplets, Timer, TrendingUp, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { vitalsApi } from '../../lib/api/vitals';
import { useUpdateVitals, useDeleteVitals } from '../../lib/hooks/useVitals';
import { LoadingState } from '../common/LoadingState';
import { formatDate, formatTime } from '../common/utils';
import { PatientLayout } from './PatientLayout';

interface VitalsManagementProps {
  patientId?: number;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

export function VitalsManagement({ patientId, onNavigate, onLogout }: VitalsManagementProps) {
  const [editingVital, setEditingVital] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Fetch vitals (last 7 days)
  const [vitalsData, setVitalsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // React Query mutations
  const updateVitalsMutation = useUpdateVitals();
  const deleteVitalsMutation = useDeleteVitals();

  const fetchVitals = async () => {
    try {
      setIsLoading(true);
      const data = await vitalsApi.getMyVitals({ days: 7 });
      setVitalsData(data?.vitals || []);
    } catch (error) {
      console.error('❌ VitalsManagement - Failed to fetch vitals:', error);
      setVitalsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchVitals();
  }, []);

  const refetch = fetchVitals;

  // ✅ FIX: Reverse vitals data for chronological order in charts
  // Backend returns newest first (desc), but charts need oldest first (asc)
  const chartData = useMemo(() => {
    if (!vitalsData || vitalsData.length === 0) return [];
    return [...vitalsData].reverse(); // Create reversed copy for charts
  }, [vitalsData]);

  // Form state
  const [spo2, setSpo2] = useState('');
  const [heartRate, setHeartRate] = useState('');
  const [bpSystolic, setBpSystolic] = useState('');
  const [bpDiastolic, setBpDiastolic] = useState('');
  const [temperature, setTemperature] = useState('');
  const [weight, setWeight] = useState('');
  const [breathHoldTime, setBreathHoldTime] = useState('');

  // Validation functions
  const getSpO2Status = (value: string) => {
    const num = parseFloat(value);
    if (num >= 95) return { label: 'Normal', color: 'text-[#14CAF4]', bg: 'bg-[#14CAF4]/10' };
    if (num >= 90) return { label: 'Low', color: 'text-accent', bg: 'bg-accent/10' };
    return { label: 'Critical', color: 'text-destructive', bg: 'bg-destructive/10' };
  };

  const getHRStatus = (value: string) => {
    const num = parseFloat(value);
    if (num >= 60 && num <= 100) return { label: 'Normal', color: 'text-[#14CAF4]', bg: 'bg-[#14CAF4]/10' };
    if (num < 60) return { label: 'Low', color: 'text-accent', bg: 'bg-accent/10' };
    return { label: 'High', color: 'text-destructive', bg: 'bg-destructive/10' };
  };

  const getBPStatus = (systolic: string, diastolic: string) => {
    const sys = parseFloat(systolic);
    const dia = parseFloat(diastolic);
    if (sys < 120 && dia < 80) return { label: 'Normal', color: 'text-[#14CAF4]', bg: 'bg-[#14CAF4]/10' };
    if (sys < 130 && dia < 80) return { label: 'Elevated', color: 'text-accent', bg: 'bg-accent/10' };
    return { label: 'High', color: 'text-destructive', bg: 'bg-destructive/10' };
  };

  const getTempStatus = (value: string) => {
    const num = parseFloat(value);
    if (num >= 97 && num <= 99) return { label: 'Normal', color: 'text-[#14CAF4]', bg: 'bg-[#14CAF4]/10' };
    if (num < 97) return { label: 'Low', color: 'text-accent', bg: 'bg-accent/10' };
    return { label: 'Fever', color: 'text-destructive', bg: 'bg-destructive/10' };
  };

  const resetForm = () => {
    setSpo2('');
    setHeartRate('');
    setBpSystolic('');
    setBpDiastolic('');
    setTemperature('');
    setWeight('');
    setBreathHoldTime('');
  };

  const handleSave = async () => {
    if (!spo2 || !heartRate || !bpSystolic || !bpDiastolic) {
      toast.error('Required fields missing', {
        description: 'Please enter SpO₂, Heart Rate, and Blood Pressure',
      });
      return;
    }

    try {
      await vitalsApi.recordMyVitals({
        spo2: parseFloat(spo2),
        heart_rate: parseFloat(heartRate),
        systolic_bp: parseFloat(bpSystolic),
        diastolic_bp: parseFloat(bpDiastolic),
        temperature: temperature ? parseFloat(temperature) : undefined,
        weight: weight ? parseFloat(weight) : undefined,
      });

      toast.success('Vitals logged successfully!');
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to log vitals');
    }
  };

  const handleEdit = async () => {
    if (!editingVital) return;

    try {
      await updateVitalsMutation.mutateAsync({
        vitalsId: editingVital.id,
        data: {
          spo2: spo2 ? parseFloat(spo2) : undefined,
          heart_rate: heartRate ? parseFloat(heartRate) : undefined,
          systolic_bp: bpSystolic ? parseFloat(bpSystolic) : undefined,
          diastolic_bp: bpDiastolic ? parseFloat(bpDiastolic) : undefined,
          temperature: temperature ? parseFloat(temperature) : undefined,
          weight: weight ? parseFloat(weight) : undefined,
        }
      });

      setEditingVital(null);
      resetForm();
      refetch();
    } catch (error: any) {
      // Error handled by mutation hook
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    try {
      await deleteVitalsMutation.mutateAsync(deletingId);
      setDeletingId(null);
      refetch();
    } catch (error: any) {
      // Error handled by mutation hook
    }
  };

  const openEditDialog = (vital: any) => {
    setEditingVital(vital);
    setSpo2(vital.spo2?.toString() || '');
    setHeartRate(vital.heart_rate?.toString() || '');
    setBpSystolic(vital.systolic_bp?.toString() || '');
    setBpDiastolic(vital.diastolic_bp?.toString() || '');
    setTemperature(vital.temperature?.toString() || '');
    setWeight(vital.weight?.toString() || '');
  };

  const canEdit = (vital: any) => {
    const vitalDate = new Date(vital.recorded_at);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - vitalDate.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  if (isLoading) {
    return (
      <PatientLayout currentView="vitals" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading vitals..." />
      </PatientLayout>
    );
  }

  const recentVitals = vitalsData.filter((v: any) => canEdit(v));

  return (
    <PatientLayout currentView="vitals" onNavigate={onNavigate} onLogout={onLogout}>
      <div className="p-4 pb-20 space-y-4 max-w-2xl mx-auto">
      {/* ORIGINAL DESIGN: Input Form */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Enter Today's Readings
          </CardTitle>
          <p className="text-muted-foreground">* Required fields</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Heart Rate Input */}
          <div className="space-y-2">
            <Label htmlFor="heartRate" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-accent" />
              </div>
              Heart Rate *
            </Label>
            <div className="relative">
              <Input
                id="heartRate"
                type="number"
                placeholder="Enter heart rate"
                value={heartRate}
                onChange={(e) => setHeartRate(e.target.value)}
                className="rounded-xl pl-4 pr-12 h-12 bg-white border-2 border-border"
                min="0"
                max="200"
              />
              <span className="absolute right-4 top-3 text-muted-foreground">bpm</span>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground">Normal range: 60-100 bpm</p>
              {heartRate && (
                <Badge variant="outline" className={`${getHRStatus(heartRate).bg} ${getHRStatus(heartRate).color} border-0`}>
                  {getHRStatus(heartRate).label}
                </Badge>
              )}
            </div>
          </div>

          {/* SpO2 Input */}
          <div className="space-y-2">
            <Label htmlFor="spo2" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#14CAF4]/10 flex items-center justify-center">
                <Activity className="w-5 h-5 text-[#14CAF4]" />
              </div>
              Oxygen Saturation (SpO₂) *
            </Label>
            <div className="relative">
              <Input
                id="spo2"
                type="number"
                placeholder="Enter SpO₂ reading"
                value={spo2}
                onChange={(e) => setSpo2(e.target.value)}
                className="rounded-xl pl-4 pr-12 h-12 bg-white border-2 border-border"
                min="0"
                max="100"
              />
              <span className="absolute right-4 top-3 text-muted-foreground">%</span>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground">Normal range: 95-100%</p>
              {spo2 && (
                <Badge variant="outline" className={`${getSpO2Status(spo2).bg} ${getSpO2Status(spo2).color} border-0`}>
                  {getSpO2Status(spo2).label}
                </Badge>
              )}
            </div>
          </div>

          {/* Blood Pressure Input */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-primary" />
              </div>
              Blood Pressure (mmHg) *
            </Label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  type="number"
                  placeholder="Systolic"
                  value={bpSystolic}
                  onChange={(e) => setBpSystolic(e.target.value)}
                  className="rounded-xl h-12 bg-white border-2 border-border"
                  min="0"
                  max="250"
                />
                <span className="absolute right-3 top-3 text-muted-foreground">sys</span>
              </div>
              <span className="flex items-center text-foreground">/</span>
              <div className="flex-1 relative">
                <Input
                  type="number"
                  placeholder="Diastolic"
                  value={bpDiastolic}
                  onChange={(e) => setBpDiastolic(e.target.value)}
                  className="rounded-xl h-12 bg-white border-2 border-border"
                  min="0"
                  max="150"
                />
                <span className="absolute right-3 top-3 text-muted-foreground">dia</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground">Normal: {'<'}120/80 mmHg</p>
              {bpSystolic && bpDiastolic && (
                <Badge variant="outline" className={`${getBPStatus(bpSystolic, bpDiastolic).bg} ${getBPStatus(bpSystolic, bpDiastolic).color} border-0`}>
                  {getBPStatus(bpSystolic, bpDiastolic).label}
                </Badge>
              )}
            </div>
          </div>

          {/* Body Temperature Input */}
          <div className="space-y-2">
            <Label htmlFor="temperature" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#14CAF4]/10 flex items-center justify-center">
                <Thermometer className="w-5 h-5 text-[#14CAF4]" />
              </div>
              Body Temperature
            </Label>
            <div className="relative">
              <Input
                id="temperature"
                type="number"
                step="0.1"
                placeholder="Enter temperature"
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                className="rounded-xl pl-4 pr-12 h-12 bg-white border-2 border-border"
                min="95"
                max="105"
              />
              <span className="absolute right-4 top-3 text-muted-foreground">°F</span>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground">Normal range: 97-99°F</p>
              {temperature && (
                <Badge variant="outline" className={`${getTempStatus(temperature).bg} ${getTempStatus(temperature).color} border-0`}>
                  {getTempStatus(temperature).label}
                </Badge>
              )}
            </div>
          </div>

          {/* Weight Input */}
          <div className="space-y-2">
            <Label htmlFor="weight" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Weight className="w-5 h-5 text-accent" />
              </div>
              Body Weight
            </Label>
            <div className="relative">
              <Input
                id="weight"
                type="number"
                step="0.1"
                placeholder="Enter weight"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="rounded-xl pl-4 pr-12 h-12 bg-white border-2 border-border"
                min="0"
                max="300"
              />
              <span className="absolute right-4 top-3 text-muted-foreground">kg</span>
            </div>
            <p className="text-muted-foreground">Track weight changes over time</p>
          </div>

          {/* Breath Hold Time Input */}
          <div className="space-y-2">
            <Label htmlFor="breathHold" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Timer className="w-5 h-5 text-primary" />
              </div>
              Breath-Hold Time (Optional)
            </Label>
            <div className="relative">
              <Input
                id="breathHold"
                type="number"
                placeholder="Enter breath-hold duration"
                value={breathHoldTime}
                onChange={(e) => setBreathHoldTime(e.target.value)}
                className="rounded-xl pl-4 pr-12 h-12 bg-white border-2 border-border"
                min="0"
              />
              <span className="absolute right-4 top-3 text-muted-foreground">sec</span>
            </div>
            <p className="text-muted-foreground">Track your lung capacity improvement</p>
          </div>

          <Button
            onClick={handleSave}
            className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
            disabled={!spo2 || !heartRate || !bpSystolic || !bpDiastolic}
          >
            Save Vitals
          </Button>
        </CardContent>
      </Card>

      {/* ORIGINAL DESIGN: Weekly Progress */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Your Weekly Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* SpO2 Trend */}
          <div>
            <h3 className="text-[#14CAF4] mb-3">SpO₂ Trend (Last 7 Days)</h3>
            <div className="h-48 bg-white rounded-xl p-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#494949" />
                  <YAxis domain={[90, 100]} tick={{ fontSize: 12 }} stroke="#494949" />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="spo2"
                    stroke="#14CAF4"
                    strokeWidth={3}
                    dot={{ fill: '#14CAF4', r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Heart Rate Trend */}
          <div>
            <h3 className="text-accent mb-3">Heart Rate Trend (Last 7 Days)</h3>
            <div className="h-48 bg-white rounded-xl p-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#494949" />
                  <YAxis domain={[60, 90]} tick={{ fontSize: 12 }} stroke="#494949" />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="heart_rate"
                    stroke="#F07D13"
                    strokeWidth={3}
                    dot={{ fill: '#F07D13', r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="p-4 bg-[#14CAF4]/10 rounded-xl">
              <p className="text-muted-foreground">Avg SpO₂</p>
              <p className="text-[#14CAF4]">
                {vitalsData && vitalsData.length > 0
                  ? Math.round(vitalsData.reduce((sum, v) => sum + v.spo2, 0) / vitalsData.length)
                  : '--'}%
              </p>
            </div>
            <div className="p-4 bg-accent/10 rounded-xl">
              <p className="text-muted-foreground">Avg Heart Rate</p>
              <p className="text-accent">
                {vitalsData && vitalsData.length > 0
                  ? Math.round(vitalsData.reduce((sum, v) => sum + v.heart_rate, 0) / vitalsData.length)
                  : '--'} bpm
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ORIGINAL DESIGN: Tips Card */}
      <Card className="shadow-lg bg-[#FEEBDA]">
        <CardContent className="p-6">
          <h3 className="text-primary mb-3">📝 Tips for Accurate Readings</h3>
          <ul className="space-y-2 text-foreground">
            <li className="flex gap-2">
              <span className="text-accent">•</span>
              <span>Measure at the same time each day (preferably morning)</span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent">•</span>
              <span>Rest for 5 minutes before measuring vitals</span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent">•</span>
              <span>Keep your finger still during SpO₂ reading</span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent">•</span>
              <span>Sit upright with feet flat for blood pressure</span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent">•</span>
              <span>Avoid caffeine 30 minutes before measurements</span>
            </li>
            <li className="flex gap-2">
              <span className="text-accent">•</span>
              <span>Weigh yourself on the same scale, without shoes</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* NEW SECTION: Recent Vitals for Edit/Delete */}
      {recentVitals.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Recent Vitals (Last 7 Days)
            </CardTitle>
            <p className="text-muted-foreground">You can edit or delete vitals from the last 7 days</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Edit/Delete Info Alert */}
            <Alert className="bg-[#14CAF4]/10 border-[#14CAF4]">
              <AlertCircle className="h-4 w-4 text-[#14CAF4]" />
              <AlertDescription className="text-[#14CAF4]">
                Tap the edit or delete icons to update or remove vitals records.
              </AlertDescription>
            </Alert>

            {/* Vitals List */}
            <div className="space-y-3">
              {recentVitals.map((vital: any) => (
                <Card key={vital.id} className="shadow-sm border-2">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="text-foreground">
                          {formatDate(vital.recorded_at)} at {formatTime(vital.recorded_at)}
                        </h4>
                        <p className="text-muted-foreground text-sm">{vital.recorded_by || 'Self-reported'}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(vital)}
                        >
                          <Edit2 className="w-4 h-4 text-primary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingId(vital.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-[#14CAF4]" />
                        <div>
                          <p className="text-muted-foreground text-xs">SpO₂</p>
                          <p className="text-foreground">{vital.spo2}%</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-accent" />
                        <div>
                          <p className="text-muted-foreground text-xs">Heart Rate</p>
                          <p className="text-foreground">{vital.heart_rate} bpm</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Droplets className="w-4 h-4 text-primary" />
                        <div>
                          <p className="text-muted-foreground text-xs">Blood Pressure</p>
                          <p className="text-foreground">{vital.systolic_bp}/{vital.diastolic_bp}</p>
                        </div>
                      </div>

                      {vital.temperature && (
                        <div className="flex items-center gap-2">
                          <Thermometer className="w-4 h-4 text-[#14CAF4]" />
                          <div>
                            <p className="text-muted-foreground text-xs">Temperature</p>
                            <p className="text-foreground">{vital.temperature}°C</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {vital.notes && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-muted-foreground text-xs mb-1">Notes</p>
                        <p className="text-foreground text-sm">{vital.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingVital} onOpenChange={(open) => !open && setEditingVital(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Vitals</DialogTitle>
            <DialogDescription>Update your vital signs measurements</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit_spo2">SpO₂ (%)</Label>
                <Input
                  id="edit_spo2"
                  type="number"
                  min="0"
                  max="100"
                  value={spo2}
                  onChange={(e) => setSpo2(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="edit_heart_rate">Heart Rate (bpm)</Label>
                <Input
                  id="edit_heart_rate"
                  type="number"
                  value={heartRate}
                  onChange={(e) => setHeartRate(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="edit_systolic_bp">Systolic BP (mmHg)</Label>
                <Input
                  id="edit_systolic_bp"
                  type="number"
                  value={bpSystolic}
                  onChange={(e) => setBpSystolic(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="edit_diastolic_bp">Diastolic BP (mmHg)</Label>
                <Input
                  id="edit_diastolic_bp"
                  type="number"
                  value={bpDiastolic}
                  onChange={(e) => setBpDiastolic(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="edit_temperature">Temperature (°C)</Label>
                <Input
                  id="edit_temperature"
                  type="number"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label htmlFor="edit_weight">Weight (kg)</Label>
                <Input
                  id="edit_weight"
                  type="number"
                  step="0.1"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingVital(null)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleEdit}
              disabled={updateVitalsMutation.isPending}
              className="bg-accent hover:bg-accent/90 rounded-xl"
            >
              {updateVitalsMutation.isPending ? 'Updating...' : 'Update Vitals'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vitals Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this vitals record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleteVitalsMutation.isPending}
            >
              {deleteVitalsMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </PatientLayout>
  );
}
